import { Badge } from "@d12labs/ui/badge";
import { EmptyState } from "@d12labs/ui/empty-state";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { Pager } from "@d12labs/ui/pager";
import { Pill } from "@d12labs/ui/pill";
import { cn } from "@d12labs/utils";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Activity, Download, Film, Sparkles } from "lucide-react";
import { useState } from "react";
import { meetingPerfApi } from "../api";

/** One background-job feed row, derived from the server's route def. */
type ActivityItem = Awaited<ReturnType<typeof meetingPerfApi.activity>>["items"][number];

type StatusFilter = "all" | "pending" | "processing" | "failed" | "completed";

const KIND_META: Record<ActivityItem["kind"], { label: string; icon: typeof Sparkles }> = {
	analysis: { label: "Analysis", icon: Sparkles },
	import: { label: "Fireflies import", icon: Download },
	media: { label: "Media download", icon: Film },
};

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
	{ key: "all", label: "All" },
	{ key: "pending", label: "Queued" },
	{ key: "processing", label: "Processing" },
	{ key: "failed", label: "Failed" },
	{ key: "completed", label: "Completed" },
];

/** Lifecycle chip — queued grey, processing lavender (pulsing), done green, failed red. */
function StatusChip({ status }: { status: string }) {
	if (status === "processing") {
		return (
			<span className="inline-flex flex-none items-center gap-1.5 rounded-full border border-primary/40 bg-primary/15 px-2 py-0.5 text-meta font-medium text-primary-hover">
				<span className="size-1.5 animate-pulse rounded-full bg-primary" />
				Processing
			</span>
		);
	}
	if (status === "completed") {
		return (
			<span className="inline-flex flex-none items-center gap-1.5 rounded-full border border-semantic-success/30 bg-semantic-success/10 px-2 py-0.5 text-meta font-medium text-semantic-success">
				<span className="size-1.5 rounded-full bg-semantic-success" />
				Completed
			</span>
		);
	}
	if (status === "failed") {
		return (
			<span className="inline-flex flex-none items-center gap-1.5 rounded-full border border-destructive/40 bg-destructive/10 px-2 py-0.5 text-meta font-medium text-destructive">
				<span className="size-1.5 rounded-full bg-destructive" />
				Failed
			</span>
		);
	}
	return (
		<span className="inline-flex flex-none items-center gap-1.5 rounded-full border border-hairline-strong bg-surface-2 px-2 py-0.5 text-meta font-medium text-ink-muted">
			<span className="size-1.5 rounded-full bg-ink-tertiary" />
			Queued
		</span>
	);
}

/** Compact relative timestamp — "just now", "4m ago", "2h ago", else a date. */
function timeAgo(iso: string): string {
	const diffSec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
	if (diffSec < 60) return "just now";
	if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
	if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
	return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Countdown to a future timestamp — for retry-backoff "retry in Xm" labels. */
function timeUntil(iso: string): string {
	const diffSec = Math.floor((new Date(iso).getTime() - Date.now()) / 1000);
	if (diffSec < 60) return "under a minute";
	if (diffSec < 3600) return `${Math.ceil(diffSec / 60)}m`;
	return `${Math.ceil(diffSec / 3600)}h`;
}

const PAGE_SIZE = 50;
const NO_COUNTS: Record<StatusFilter, number> = {
	all: 0,
	pending: 0,
	processing: 0,
	failed: 0,
	completed: 0,
};

export function ActivityPage() {
	const [filter, setFilter] = useState<StatusFilter>("all");
	// 0-based locally; the server and <Pager> are 1-based.
	const [page, setPage] = useState(0);

	// Live feed — background work changes by the second while imports run, so
	// poll while the page is open (and only while it's visible). The server
	// filters and pages; `counts` are totals across ALL jobs, not this page.
	const activity = useQuery({
		queryKey: ["meeting-perf", "activity", filter, page],
		queryFn: () =>
			meetingPerfApi.activity({
				query: {
					page: page + 1,
					pageSize: PAGE_SIZE,
					...(filter === "all" ? {} : { status: filter }),
				},
			}),
		placeholderData: keepPreviousData,
		refetchInterval: 4000,
		refetchIntervalInBackground: false,
	});

	const visible = activity.data?.items ?? [];
	const counts = activity.data?.counts ?? NO_COUNTS;
	const pageCount = activity.data?.pageCount ?? 1;
	const inFlight = counts.pending + counts.processing;
	const pickFilter = (f: StatusFilter) => {
		setFilter(f);
		setPage(0);
	};

	return (
		<>
			<PageHeader
				icon={Activity}
				title="Activity"
				badges={inFlight > 0 ? <Badge tone="primary">{inFlight} in flight</Badge> : undefined}
				subtitle="Background work — Fireflies imports, speaker analyses, and media downloads."
			/>
			<PageBody className="p-6">
				<div className="mx-auto w-full max-w-5xl">
					<div className="mb-3 flex flex-wrap items-center justify-between gap-3">
						<div className="flex items-center gap-2">
							{STATUS_FILTERS.map((f) => (
								<Pill key={f.key} active={filter === f.key} onClick={() => pickFilter(f.key)}>
									{f.label}
									{counts[f.key] > 0 ? (
										<span className="ml-1.5 tabular-nums text-ink-tertiary">{counts[f.key]}</span>
									) : null}
								</Pill>
							))}
						</div>
						<span className="text-meta text-ink-subtle">
							{activity.isFetching ? "Refreshing…" : "Auto-refreshes every 4s"}
						</span>
					</div>

					{activity.error ? (
						<p className="text-body-sm text-destructive">Could not load activity.</p>
					) : activity.isLoading ? (
						<p className="text-body-sm text-ink-subtle">Loading…</p>
					) : visible.length === 0 ? (
						<div className="rounded-lg border border-hairline bg-surface-1">
							<EmptyState
								icon={Activity}
								title={filter === "all" ? "Nothing in flight" : "Nothing here"}
								body={
									filter === "all"
										? "Process a Fireflies meeting or run an analysis and it will show up here."
										: "No background work with this status."
								}
							/>
						</div>
					) : (
						<div className="overflow-hidden rounded-lg border border-hairline bg-surface-1">
							<ul className="divide-y divide-hairline">
								{visible.map((item) => {
									const meta = KIND_META[item.kind];
									const Icon = meta.icon;
									return (
										<li key={item.id} className="flex flex-col gap-1 px-4 py-3">
											<div className="grid grid-cols-[1fr_auto] items-center gap-4">
												<div className="flex min-w-0 items-center gap-2.5">
													<span className="flex size-7 flex-none items-center justify-center rounded-md border border-hairline bg-surface-2 text-ink-subtle">
														<Icon className="size-3.5" />
													</span>
													<div className="min-w-0">
														<div className="flex items-center gap-2">
															<span className="truncate text-body-sm font-medium text-ink">
																{item.label}
															</span>
															<StatusChip status={item.status} />
														</div>
														<div className="text-meta tabular-nums text-ink-subtle">
															{meta.label}
															{item.detail ? ` · ${item.detail}` : ""}
															{item.attempts > 1
																? ` · attempt ${item.attempts}/${item.maxAttempts}`
																: ""}
														</div>
													</div>
												</div>
												<span
													className={cn(
														"text-meta tabular-nums text-ink-tertiary",
														item.status === "processing" && "text-primary-hover",
													)}
												>
													{item.status === "completed" && item.completedAt
														? `done ${timeAgo(item.completedAt)}`
														: item.status === "pending" &&
																new Date(item.runAt).getTime() > Date.now()
															? `retry in ${timeUntil(item.runAt)}`
															: timeAgo(item.createdAt)}
												</span>
											</div>
											{item.status === "failed" && item.lastError ? (
												<p className="ml-[38px] truncate text-meta text-destructive">
													{item.lastError}
												</p>
											) : null}
										</li>
									);
								})}
							</ul>
							{pageCount > 1 ? (
								<div className="border-t border-hairline px-4 py-3">
									<Pager
										page={page + 1}
										pageCount={pageCount}
										onPageChange={(n) => setPage(n - 1)}
										label={`${activity.data?.total ?? 0} jobs`}
										variant="ghost"
									/>
								</div>
							) : null}
						</div>
					)}
				</div>
			</PageBody>
		</>
	);
}
